import { Box, createStyles, Group, Stack } from '@mantine/core';
import { useContinuePrompt } from '@repo/api-codegen';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { Button, Text } from '@repo/foundations';
import { isEmpty } from 'lodash-es';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import {
	useIntegration,
	useIntegrationList,
} from '../../../api/hooks/integration';
import { useIntegrationOAuthConnect } from '../../../features/IntegrationOauthConnect';
import type { BuiltinIntegrationSpec } from '../../../interfaces/IntegrationSpec';
import { UserLevelCredentialsManager } from '../../Integration/IntegrationPage/TabPanels/PreferencesPanel/UserLevelCredentialsManager';
import { RichEditor } from '../../RichEditor';
import { ArtifactRenderer } from './components/ArtifactRenderer';
import { ChartRenderer } from './components/ChartRenderer';
import { ThoughtCollapse } from './components/ThoughtCollapse';
import type { ContentSegment } from './utils';
import { detectOAuthError, processContentWithEmbedded } from './utils';

const useStyles = createStyles(
	(theme, { isReasoning }: { isReasoning: boolean }) => ({
		editor: {
			'& .ProseMirror': {
				color: isReasoning
					? theme.other.getColor('text/secondary/default')
					: theme.other.getColor('text/primary/default'),
			},
			'& .ProseMirror > a.heading-name': {
				display: 'none',
			},
			'#thinking-section-content & .ProseMirror': {
				fontSize: theme.fontSizes.sm,
				'& br.ProseMirror-trailingBreak': {
					display: 'none',
				},
			},
		},
	})
);

interface AnimatedContentProps {
	content: string;
	isTyping?: boolean;
	typingSpeed?: number; // Characters per second
	onTypingComplete?: () => void;
	cacheKey: string;
	isReasoning?: boolean;
}

// Component for rendering a Max Iterations message with a continue button
function MaxIterationsRenderer({ promptId }: { promptId: string }) {
	const continuePrompt = useContinuePrompt({
		onSuccess: () => {
			window.location.reload();
		},
	});

	const handleContinue = () => {
		if (promptId) {
			continuePrompt.mutate({
				pathParams: {
					promptId,
				},
			});
		}
	};

	return (
		<Stack spacing="xs">
			<Text size="sm">
				Maximum iterations have been reached. You can continue by pressing the
				button below.
			</Text>
			<Group position="left">
				<Button
					onClick={handleContinue}
					loading={continuePrompt.isPending}
					disabled={continuePrompt.isPending}
				>
					Continue
				</Button>
			</Group>
		</Stack>
	);
}

// Component for rendering OAuth authentication required message
function OauthRequiredRenderer({ integrationId }: { integrationId?: string }) {
	// Fetch the specific integration if we have an ID
	const { data: integration } = useIntegration({
		id: integrationId ?? '',
		options: {
			enabled: !!integrationId,
		},
	});

	// If no specific integration ID provided, find integrations with OAuth enabled
	const { data: integrationsData } = useIntegrationList({
		options: {
			enabled: !integrationId,
		},
	});

	// Find an integration with OAuth enabled if no specific ID provided
	const fallbackIntegration = integrationsData?.results?.find(
		(i) => (i.credentials as Record<string, unknown>)?.oauth_enabled
	);

	const targetIntegration = integration || fallbackIntegration;
	const oauthMutation = useIntegrationOAuthConnect(
		targetIntegration?.id,
		targetIntegration?.type,
		{ openInNewTab: true }
	);

	const containerStyle = (theme: any) => ({
		padding: theme.spacing.md,
		borderRadius: theme.other.borderRadius.md,
		border: `1px solid ${theme.other.getColor('border/critical/default')}`,
		marginTop: theme.spacing.md,
		marginBottom: theme.spacing.md,
		background: theme.other.getColor('surface/critical/default'),
	});

	return (
		<Box sx={containerStyle}>
			<Stack spacing="xs">
				<Text size="sm" color="text/primary/default" weight="semibold">
					Authentication required
				</Text>
				<Text size="sm" color="text/secondary/default">
					Connect your account to run queries
				</Text>
				<Group spacing="sm">
					<Button
						variant="primary"
						size="sm"
						rightIconName="arrowUpRight"
						onClick={() => oauthMutation.mutate()}
						loading={oauthMutation.isPending}
						disabled={!targetIntegration?.id}
					>
						Connect
					</Button>
				</Group>
			</Stack>
		</Box>
	);
}

// Component for rendering integration credentials management
function IntegrationCredentialsRenderer({
	integrationId,
}: {
	integrationId: string;
}) {
	const { data: integration, isLoading } = useIntegration({
		id: integrationId,
	});

	const containerStyle = (theme: any) => ({
		padding: theme.spacing.md,
		borderRadius: theme.other.borderRadius.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		marginTop: theme.spacing.md,
		marginBottom: theme.spacing.md,
		background: theme.other.getColor('surface/primary/default'),
	});

	if (isLoading) {
		return (
			<Box sx={containerStyle}>
				<Stack spacing="xs">
					<Text size="sm">Loading integration credentials...</Text>
				</Stack>
			</Box>
		);
	}

	if (!integration) {
		return (
			<Box sx={containerStyle}>
				<Stack spacing="xs">
					<Text size="sm" color="text/critical/default">
						Integration not found
					</Text>
				</Stack>
			</Box>
		);
	}

	const spec = integrationList.find(
		(spec: BuiltinIntegrationSpec) => spec.type === integration.type
	);

	if (!spec) {
		return (
			<Box sx={containerStyle}>
				<Stack spacing="xs">
					<Text size="sm" color="text/critical/default">
						Integration specification not found
					</Text>
				</Stack>
			</Box>
		);
	}

	return (
		<Box sx={containerStyle}>
			<UserLevelCredentialsManager integration={integration} spec={spec} />
		</Box>
	);
}

export const AnimatedContent = memo(
	({
		content,
		isTyping = false,
		typingSpeed = 400,
		onTypingComplete,
		cacheKey,
		isReasoning = false,
	}: AnimatedContentProps) => {
		const { classes } = useStyles({ isReasoning });
		const [displayedContent, setDisplayedContent] = useState('');
		const [contentSegments, setContentSegments] = useState<
			Array<ContentSegment>
		>([]);

		// Keep track of the animation state
		const animationState = useRef({
			intervalId: null as NodeJS.Timeout | null,
			currentLength: 0,
			isAnimating: false,
		});

		// Load saved progress on mount
		useEffect(() => {
			const savedProgress = localStorage.getItem(`typing-progress-${cacheKey}`);
			if (savedProgress) {
				const progress = parseInt(savedProgress, 10);
				if (!isNaN(progress) && progress > 0 && progress <= content.length) {
					animationState.current.currentLength = progress;
					setDisplayedContent(content.slice(0, progress));
				}
			}
		}, [cacheKey, content]);

		// Process the displayed content to extract chart and artifact tags
		useEffect(() => {
			if (displayedContent) {
				setContentSegments(processContentWithEmbedded(displayedContent));
			} else {
				setContentSegments([]);
			}
		}, [displayedContent, setContentSegments]);

		// Handle typing animation
		useEffect(() => {
			if (isEmpty(content)) return;

			const state = animationState.current;

			// If not typing, show full content immediately
			if (!isTyping) {
				if (state.intervalId) {
					clearInterval(state.intervalId);
					state.intervalId = null;
				}
				setDisplayedContent(content);
				state.currentLength = content.length;
				state.isAnimating = false;
				localStorage.setItem(
					`typing-progress-${cacheKey}`,
					content.length.toString()
				);
				return;
			}

			// If we're already animating, just update the target content
			if (state.isAnimating) {
				return;
			}

			// Start a new animation
			state.isAnimating = true;
			const interval = 1000 / typingSpeed;

			const animate = () => {
				state.currentLength += 1;

				if (state.currentLength <= content.length) {
					setDisplayedContent(content.slice(0, state.currentLength));
					localStorage.setItem(
						`typing-progress-${cacheKey}`,
						state.currentLength.toString()
					);
				} else {
					// Animation complete
					if (state.intervalId) {
						clearInterval(state.intervalId);
						state.intervalId = null;
					}
					state.isAnimating = false;
					onTypingComplete?.();
				}
			};

			// Clear any existing interval
			if (state.intervalId) {
				clearInterval(state.intervalId);
			}

			// Start new interval
			state.intervalId = setInterval(animate, interval);

			// Cleanup
			return () => {
				if (state.intervalId) {
					clearInterval(state.intervalId);
					state.intervalId = null;
				}
			};
		}, [content, isTyping, typingSpeed, onTypingComplete, cacheKey]);

		const renderTextContent = (text: string) => {
			// Check for OAuth error in text content
			const oauthError = detectOAuthError(text);

			if (isReasoning) {
				return (
					<>
						<ThoughtCollapse text={text} />
						{oauthError.hasOAuthError && (
							<OauthRequiredRenderer integrationId={oauthError.integrationId} />
						)}
					</>
				);
			}

			return (
				<>
					<RichEditor readOnly value={text} className={classes.editor} />
					{oauthError.hasOAuthError && (
						<OauthRequiredRenderer integrationId={oauthError.integrationId} />
					)}
				</>
			);
		};

		const renderSegment = (segment: ContentSegment) => {
			if (segment.type === 'text') {
				return renderTextContent(segment.content);
			} else if (segment.type === 'chart') {
				return <ChartRenderer chartId={segment.content} />;
			} else if (segment.type === 'artifact') {
				return <ArtifactRenderer artifactId={segment.content} />;
			} else if (segment.type === 'maxiterations') {
				return <MaxIterationsRenderer promptId={segment.content} />;
			} else if (segment.type === 'proposals-group') {
				// Special case: proposals will be rendered after the chat is completed
				return null;
			} else if (segment.type === 'integration-credentials') {
				return (
					<IntegrationCredentialsRenderer integrationId={segment.content} />
				);
			} else if (segment.type === 'oauth-required') {
				return <OauthRequiredRenderer integrationId={segment.content} />;
			}
			return null;
		};

		return (
			<Box>
				{contentSegments.length > 0
					? contentSegments.map((segment, index) => (
							<Fragment key={`segment-${index}`}>
								{renderSegment(segment)}
							</Fragment>
						))
					: renderTextContent(displayedContent)}
			</Box>
		);
	}
);
